import { type ReactElement } from "react";
import { PresenceStack } from "@compositions/index";
import { useObservedRecordPresence } from "./useRecordPresence";

// ---------------------------------------------------------------------------
// Per-row presence facepiles for list surfaces.
//
// These live in the experience-shell (not the presentational composition)
// because each row must call the presence hook, which subscribes the row to
// the record's SignalR group with a `watch` hold — observing the roster
// without counting the list viewer as present. A dedicated component per row
// is what lets the hook run once per rendered item; list compositions stay
// presentational and receive these via their `renderPresence` slot.
// ---------------------------------------------------------------------------

export interface WorkItemRowPresenceProps {
    readonly workItemId: string;
    /**
     * Galileo user id of the work item's current lock holder, already
     * validated against the read-time expiry guard by the caller (pass
     * null when the item is free or the lock has expired). Forwarded to the
     * facepile so the holder's avatar — if they happen to be present —
     * carries a lock glyph.
     */
    readonly lockedGalileoUserId?: string | null;
}

/** Facepile of users present on a task work item, for queue/list rows. */
export function WorkItemRowPresence({
    workItemId,
    lockedGalileoUserId,
}: WorkItemRowPresenceProps): ReactElement | null {
    const viewers = useObservedRecordPresence("workItem", workItemId);
    if (viewers.length === 0) {
        return null;
    }
    return (
        <PresenceStack
            members={viewers}
            size="xs"
            max={3}
            recordLabel="this task"
            {...(lockedGalileoUserId != null ? { lockedGalileoUserId } : {})}
        />
    );
}

export interface WorkflowRowPresenceProps {
    readonly workflowId: string;
}

/** Facepile of users present on a workflow, for entity/list rows. */
export function WorkflowRowPresence({ workflowId }: WorkflowRowPresenceProps): ReactElement | null {
    const viewers = useObservedRecordPresence("workflow", workflowId);
    if (viewers.length === 0) {
        return null;
    }
    return <PresenceStack members={viewers} size="xs" max={3} recordLabel="this workflow" />;
}
